import { useDownloadScanEventMedia } from '@hakimo-ui/hakimo/data-access';
import { DataVersion } from '@hakimo-ui/hakimo/types';
import { eventTracker, toast, useFileDownload } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { useState } from 'react';

function onDownloadError(error: Error) {
  // The Scan path reports through httpClient's handleResponse. Without this
  // an RG failure - expired presign, CORS - is only ever a toast the operator
  // dismisses, and never reaches Sentry.
  Sentry.captureException(error);
  toast(`Failed to download alarm media: ${error.message}`, { type: 'error' });
}

// Derived from the URL path rather than guessed, since RG alarms can be
// either a video or (when no video was captured) a still image. A presigned
// URL need not carry the key's extension, so the response's own Content-Type
// is the second opinion.
function getFileExtension(url: string, contentType?: string | null): string {
  try {
    const match = new URL(url).pathname.match(/\.([a-zA-Z0-9]+)$/);
    if (match) {
      return match[1];
    }
  } catch {
    // Not a parseable URL - fall through to the content type.
  }
  return contentType?.split(';')[0].trim().split('/')[1] || 'mp4';
}

interface DownloadButtonProps {
  loading: boolean;
  disabled?: boolean;
  onDownload: () => void;
}

function DownloadButton({
  loading,
  disabled,
  onDownload,
}: DownloadButtonProps) {
  return (
    <Button
      variant="icon"
      onClick={onDownload}
      onSideEffect={eventTracker('download_alarm_media')}
      disabled={loading || disabled}
      loading={loading}
      title="Download Media"
    >
      <ArrowDownTrayIcon className="h-5 w-5" />
    </Button>
  );
}

// The media is already an mp4/jpg at a presigned S3 URL, and the bucket allows
// CORS from the portal origins, so the browser can fetch it directly. It has to
// be fetch-then-blob: the anchor `download` attribute is ignored cross-origin,
// which would navigate to the video instead of saving it.
export function RgDownloadButton({
  alarmId,
  mediaUrl,
  debug,
}: {
  alarmId: string;
  mediaUrl?: string;
  debug?: boolean;
}) {
  const { downloadFile } = useFileDownload();
  const [loading, setLoading] = useState(false);

  // Stays mounted and merely disabled while mediaUrl is absent. Unmounting
  // it would drop `loading` mid-download - toggling Debug Mode clears the
  // media query - re-mounting it enabled and letting a second download of
  // the same alarm start.
  const onDownload = async () => {
    if (!mediaUrl) {
      return;
    }
    setLoading(true);
    try {
      // cache: 'reload' is load bearing. The <video> element has already
      // fetched this URL without an Origin header, and S3 answers those
      // with no Access-Control-Allow-Origin *and no Vary header*, so the
      // browser reuses that cached response for any later request to the
      // same URL - including this one, which then fails CORS without ever
      // hitting the network. Reloading bypasses the poisoned entry.
      const response = await fetch(mediaUrl, { cache: 'reload' });
      if (!response.ok) {
        throw new Error(`Failed to fetch media (${response.status})`);
      }
      const blob = await response.blob();
      downloadFile({
        blob,
        // Debug renders carry burnt-in boxes and overlays. Saving them
        // under the evidence filename is how one ends up attached to an
        // incident report as the real footage.
        fileName: `alarm_${alarmId}${debug ? '_debug' : ''}.${getFileExtension(
          mediaUrl,
          response.headers.get('Content-Type')
        )}`,
      });
    } catch (error) {
      onDownloadError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DownloadButton
      loading={loading}
      disabled={!mediaUrl}
      onDownload={onDownload}
    />
  );
}

// Scan media is a set of frames in a bucket with no CORS, so the gateway
// stitches them into an mp4 and streams it back.
export function ScanDownloadButton({
  alarmId,
  disabled,
  dataVersion,
}: {
  alarmId: string;
  disabled?: boolean;
  dataVersion?: DataVersion;
}) {
  const mutation = useDownloadScanEventMedia(
    alarmId,
    undefined,
    onDownloadError,
    dataVersion
  );

  return (
    <DownloadButton
      loading={mutation.isLoading}
      disabled={disabled}
      onDownload={() => mutation.mutate()}
    />
  );
}
